/**
 * 古籍课堂。文章走后端 articles.list / articles.get（公开 action，无需登录）。
 *
 * SEO：列表页与每篇文章都是独立可收录 URL（/study、/study/<slug>）。
 * 预渲染时 scripts/prerender.mjs 会在 Node 里把文章数据塞到 globalThis.__STUDY_DATA__，
 * 组件首屏直接用它渲染出正文供爬虫抓取；浏览器端再照常按需拉取/接管。
 */
import { useEffect, useState } from "react";
import { callApi } from "../api";
import { onNavClick, routeToPath } from "../router";

type Article = {
  id?: string;
  slug?: string;
  title?: string;
  category?: string;
  summary?: string;
  content?: string;
  updatedAt?: string;
};

type StudySeed = { list?: Article[]; articles?: Record<string, Article> };

function seed(): StudySeed {
  return ((globalThis as { __STUDY_DATA__?: StudySeed }).__STUDY_DATA__) || {};
}

function articleKey(a: Article): string {
  return a.slug || a.id || "";
}

function Body({ content }: { content?: string }) {
  if (!content) return <p className="empty-state">这篇暂无正文内容。</p>;
  return (
    <>
      {content.split("\n").filter(Boolean).map((line, index) => (
        <p key={index}>{line}</p>
      ))}
    </>
  );
}

/** 文章详情 /study/<slug> */
export function StudyArticlePage({
  slug,
  onNavigate,
}: {
  slug: string;
  onNavigate: (route: string) => void;
}) {
  const seededArticle = seed().articles?.[slug];
  const seededList = seed().list?.find((a) => articleKey(a) === slug);
  const [article, setArticle] = useState<Article | null>(seededArticle || seededList || null);

  useEffect(() => {
    if (article?.content) return;
    callApi<{ article?: Article }>("articles.get", { slug, id: slug }).then((r) => {
      if (r.ok && r.article) setArticle(r.article);
    });
  }, [slug]);

  return (
    <main className="page study-page">
      <nav className="breadcrumb" aria-label="面包屑">
        <a href="/" onClick={(e) => onNavClick(e, "home", onNavigate)}>首页</a>
        <span aria-hidden="true"> / </span>
        <a href={routeToPath("study")} onClick={(e) => onNavClick(e, "study", onNavigate)}>古籍课堂</a>
        <span aria-hidden="true"> / </span>
        <span aria-current="page">{article?.title ?? slug}</span>
      </nav>
      <h1>{article?.title ?? "古籍课堂"}</h1>
      {article?.category && <p className="study-category">{article.category}</p>}
      {article?.summary && <p className="legal-summary">{article.summary}</p>}
      <Body content={article?.content} />
    </main>
  );
}

/** 列表页 /study */
export function StudyPage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const seededList = seed().list;
  const [articles, setArticles] = useState<Article[]>(seededList ?? []);
  const [state, setState] = useState<"loading" | "done" | "error">(
    seededList && seededList.length ? "done" : "loading",
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    callApi<{ articles?: Article[] }>("articles.list").then((result) => {
      if (result.ok) {
        setArticles(result.articles ?? []);
        setState("done");
      } else {
        setMessage(result.error);
        setState("error");
      }
    });
  }, []);

  return (
    <main className="page study-page">
      <h1>古籍课堂</h1>
      <p className="page-summary">整理过的传统文化学习资料，用于建立基础概念与阅读原典。</p>

      {state === "loading" && <p className="empty-state">正在加载资料…</p>}
      {state === "error" && <p className="form-error">加载失败：{message}</p>}
      {state === "done" && articles.length === 0 && <p className="empty-state">暂无资料。</p>}

      <ul className="study-list">
        {articles.map((article, index) => {
          const key = articleKey(article);
          const route = key ? `study/${key}` : "study";
          return (
            <li key={article.id ?? article.slug ?? index}>
              <a href={routeToPath(route)} onClick={(e) => onNavClick(e, route, onNavigate)}>
                <strong>{article.title ?? "未命名"}</strong>
                {article.category && <span className="study-category">{article.category}</span>}
                {article.summary && <span className="study-summary">{article.summary}</span>}
              </a>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
